// ConversationHistory Component - Display and resume past conversations
// Phase 11: Fixed date calculation bug
// © 2025 precatio, llc

import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import './ConversationHistory.css';

const ConversationHistory = ({ onSelectConversation, onClose }) => {
  const [conversations, setConversations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  
  const { token } = useAuth();

  const conversationTypeLabels = {
    examen: 'Daily Examen',
    novena: 'Custom Novena',
    prayers: 'Common Prayers',
    gospelprayer: 'Gospel Prayer',
    askiggy: 'Ask Iggy',
    discernment: 'Discernment'
  };

  const conversationTypeIcons = {
    examen: '🙏',
    novena: '9️⃣',
    prayers: '🙏',
    gospelprayer: '📖',
    askiggy: '💬',
    discernment: '💡'
  };

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    try {
      const response = await fetch('/api/conversations', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load conversations');
      }

      const data = await response.json();
      setConversations(data.conversations || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    // Parse the date - handle both ISO strings and SQLite datetime format
    const date = new Date(dateString);
    const now = new Date();
    
    // Reset both to start of day for accurate day comparison
    const dateDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Calculate difference in days
    const diffMs = nowDay - dateDay;
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays > 1 && diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  const truncatePreview = (text, maxLength = 60) => {
    if (!text) return 'No messages yet';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  const handleConversationClick = (conversation) => {
    onSelectConversation({
      sessionId: conversation.session_id,
      conversationType: conversation.conversation_type
    });
  };

  return (
    <div className="history-overlay" onClick={onClose}>
      <div className="history-panel" onClick={(e) => e.stopPropagation()}>
        <div className="history-header">
          <h2>Your Conversations</h2>
          <button className="history-close" onClick={onClose}>×</button>
        </div>

        <div className="history-content">
          {isLoading && (
            <div className="history-loading">
              <p>Loading your conversations...</p>
            </div>
          )}

          {error && (
            <div className="history-error">
              <p>{error}</p>
            </div>
          )}

          {!isLoading && !error && conversations.length === 0 && (
            <div className="history-empty">
              <p>No conversations yet.</p>
              <p>Start a conversation with Iggy and it will appear here!</p>
            </div>
          )}

          {!isLoading && !error && conversations.length > 0 && (
            <div className="history-list">
              {conversations.map((conv) => (
                <button
                  key={conv.session_id}
                  className="history-item"
                  onClick={() => handleConversationClick(conv)}
                >
                  <div className="history-item-icon">
                    {conversationTypeIcons[conv.conversation_type] || '💬'}
                  </div>
                  <div className="history-item-content">
                    <div className="history-item-header">
                      <span className="history-item-type">
                        {conversationTypeLabels[conv.conversation_type] || conv.conversation_type}
                      </span>
                      <span className="history-item-date">
                        {formatDate(conv.last_message_at)}
                      </span>
                    </div>
                    <p className="history-item-preview">
                      {truncatePreview(conv.preview)}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConversationHistory;
